/* VVBETTER Digital Flagship behaviours for Beyond 7.3.
 * Source: prototypes/digital-flagship-20261005 (navigation.js, hero-campaigns.js, impact.js, client.js menu toggle).
 * Every module binds to its own root once (data-vvb-bound) and re-runs on Shopify section load in the theme editor. */
(() => {
  if (window.__vvbLoaded) return;
  window.__vvbLoaded = true;

  const once = (el, key) => {
    if (!el || el.dataset['vvb' + key]) return false;
    el.dataset['vvb' + key] = '1';
    return true;
  };

  /* ---------- Header navigation (desktop hover + keyboard + mobile accordion) ---------- */
  function initNav(scope = document) {
    const nav = scope.querySelector('#navigation');
    if (!once(nav, 'Nav')) return;
    const items = [...nav.querySelectorAll('.nav-disclosure')];
    const hoverOpened = new Set();
    const desktopHover = () => matchMedia('(min-width:781px) and (hover:hover)').matches;
    let closeTimer;
    const collapse = (except = null) => items.forEach(item => { if (item !== except) { item.open = false; hoverOpened.delete(item); } });
    for (const item of items) {
      item.addEventListener('toggle', () => { if (item.open) collapse(item); });
      item.addEventListener('pointerenter', event => {
        clearTimeout(closeTimer);
        if (event.pointerType === 'mouse' && desktopHover()) {
          if (!item.open) hoverOpened.add(item);
          collapse(item); item.open = true;
        }
      });
      item.querySelector('summary').addEventListener('click', event => {
        if (event.detail > 0 && desktopHover() && hoverOpened.has(item)) {
          event.preventDefault(); hoverOpened.delete(item); item.open = true;
        }
      });
    }
    /* only the main shortcut row closes an open mega menu; the small journal links must not, or the pointer cannot travel from a menu item down to its panel */
    nav.querySelectorAll('.nav-shortcuts .nav-direct').forEach(link => link.addEventListener('pointerenter', event => { if (event.pointerType === 'mouse') collapse(); }));
    /* the whole header counts as the menu area, so the pointer can cross empty header space on its way to a panel */
    const zone = nav.closest('.site-header') || nav;
    zone.addEventListener('pointerenter', () => clearTimeout(closeTimer));
    zone.addEventListener('pointerleave', () => {
      if (desktopHover()) closeTimer = setTimeout(() => { if (!nav.contains(document.activeElement)) collapse(); }, 170);
    });
    nav.addEventListener('focusout', event => { if (!nav.contains(event.relatedTarget)) collapse(); });
    document.addEventListener('click', event => { if (!nav.contains(event.target)) collapse(); });
    document.addEventListener('keydown', event => {
      if (event.key === 'Escape') {
        const active = items.find(item => item.open);
        if (active) { collapse(); active.querySelector('summary').focus(); event.preventDefault(); }
        else if (nav.classList.contains('open')) {
          nav.classList.remove('open');
          document.querySelector('[data-menu]')?.setAttribute('aria-expanded', 'false');
        }
      }
    });
    const menu = scope.querySelector('[data-menu]');
    if (menu) {
      menu.setAttribute('aria-label', menu.dataset.label || 'Navigation menu');
      menu.addEventListener('click', () => {
        const open = nav.classList.toggle('open');
        menu.setAttribute('aria-expanded', String(open));
        if (!open) collapse();
      });
    }
    matchMedia('(max-width:780px)').addEventListener('change', () => collapse());
  }

  /* ---------- Campaign hero (tabs, arrows, swipe, video autoplay rules) ---------- */
  function initHero(scope = document) {
    const root = scope.querySelector('.campaign-hero');
    if (!once(root, 'Hero')) return;
    const tabs = [...root.querySelectorAll('[data-campaign]')];
    const panels = [...root.querySelectorAll('.campaign-panel')];
    const films = panels.flatMap((panel, index) => {
      const video = panel.querySelector('[data-campaign-video]');
      return video ? [{ video, control: panel.querySelector('[data-campaign-motion]'), index, pausedByUser: false }] : [];
    });
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    let active = 0, visible = false;
    const label = f => { if (!f.control) return; f.control.textContent = f.video.paused ? (f.control.dataset.play || 'Play film ▶') : (f.control.dataset.pause || 'Pause film Ⅱ'); f.control.setAttribute('aria-pressed', String(!f.video.paused)); };
    const sync = () => films.forEach(f => {
      const shouldPlay = active === f.index && visible && !document.hidden && !document.querySelector('dialog[open]') && !f.pausedByUser && !reduced.matches && !navigator.connection?.saveData;
      if (shouldPlay) {
        if (f.video.dataset.src && !f.video.getAttribute('src')) f.video.src = f.video.dataset.src;
        f.video.play().then(() => label(f)).catch(() => label(f));
      } else { f.video.pause(); label(f); }
    });
    /* automatic change: fixed seconds per panel, or until a film has played once; paused while hovering, hidden, in a dialog or with reduced motion */
    const auto = root.dataset.autoplay === 'true' && panels.length > 1;
    const seconds = Math.max(3, Number(root.dataset.interval) || 8);
    const waitForFilm = root.dataset.films === 'through';
    let timer = null, hovering = false, userPaused = false;
    const canRun = () => auto && !userPaused && visible && !document.hidden && !hovering && !reduced.matches && !document.querySelector('dialog[open]');
    const pauseBtn = root.querySelector('[data-campaign-pause]');
    pauseBtn?.addEventListener('click', () => {
      userPaused = !userPaused;
      pauseBtn.setAttribute('aria-pressed', String(userPaused));
      pauseBtn.textContent = userPaused ? '▶' : '❙❙';
      pauseBtn.setAttribute('aria-label', userPaused ? (pauseBtn.dataset.labelResume || 'Resume automatic change') : (pauseBtn.dataset.labelPause || 'Pause automatic change'));
      schedule();
    });
    const schedule = () => {
      clearTimeout(timer); timer = null;
      if (!canRun()) return;
      const film = films.find(f => f.index === active);
      if (film && film.pausedByUser) return;
      timer = setTimeout(() => select(active + 1), (film && waitForFilm ? 25 : seconds) * 1000);
    };
    films.forEach(f => f.video.addEventListener('timeupdate', () => {
      if (waitForFilm && auto && f.index === active && f.video.duration && f.video.currentTime >= f.video.duration - 0.4 && canRun()) select(active + 1);
    }));
    root.addEventListener('mouseenter', () => { hovering = true; schedule(); });
    root.addEventListener('mouseleave', () => { hovering = false; schedule(); });
    root.addEventListener('focusin', () => { hovering = true; schedule(); });
    root.addEventListener('focusout', () => { hovering = false; schedule(); });
    const select = (index, focus = false) => {
      const previous = active;
      active = (index + panels.length) % panels.length;
      if (active !== previous) { const film = films.find(f => f.index === active); if (film) { try { film.video.currentTime = 0; } catch (e) { /* not loaded yet */ } } }
      panels.forEach((p, i) => { p.classList.toggle('is-active', i === active); p.toggleAttribute('inert', i !== active); p.setAttribute('aria-hidden', String(i !== active)); });
      tabs.forEach((t, i) => { t.setAttribute('aria-selected', String(i === active)); t.tabIndex = i === active ? 0 : -1; });
      const count = root.querySelector('[data-campaign-count]');
      if (count) count.textContent = String(active + 1).padStart(2, '0') + ' / ' + String(tabs.length).padStart(2, '0');
      if (focus && tabs[active]) tabs[active].focus();
      sync(); schedule();
    };
    tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => select(i));
      tab.addEventListener('keydown', e => {
        if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) {
          e.preventDefault();
          select(e.key === 'Home' ? 0 : e.key === 'End' ? panels.length - 1 : active + (e.key === 'ArrowRight' ? 1 : -1), true);
        }
      });
    });
    root.querySelector('[data-campaign-prev]')?.addEventListener('click', () => select(active - 1));
    root.querySelector('[data-campaign-next]')?.addEventListener('click', () => select(active + 1));
    films.forEach(f => {
      f.control?.addEventListener('click', () => {
        if (f.video.paused) {
          f.pausedByUser = false;
          if (f.video.dataset.src && !f.video.getAttribute('src')) f.video.src = f.video.dataset.src;
          f.video.play().then(() => label(f)).catch(() => label(f));
        } else { f.pausedByUser = true; f.video.pause(); }
        schedule();
      });
      f.video.addEventListener('play', () => label(f));
      f.video.addEventListener('pause', () => label(f));
    });
    document.addEventListener('visibilitychange', () => { sync(); schedule(); });
    reduced.addEventListener('change', () => { sync(); schedule(); });
    new IntersectionObserver(entries => { visible = entries[0].isIntersecting; sync(); schedule(); }, { threshold: .1 }).observe(root);
    let touchStart = null;
    root.addEventListener('touchstart', e => { touchStart = e.touches[0].clientX; }, { passive: true });
    root.addEventListener('touchend', e => {
      if (touchStart !== null && Math.abs(e.changedTouches[0].clientX - touchStart) > 65) select(active + (e.changedTouches[0].clientX < touchStart ? 1 : -1));
      touchStart = null;
    }, { passive: true });
    sync();
  }

  /* ---------- In-motion feed + creator filters ---------- */
  function initMotion(scope = document) {
    const feed = scope.querySelector('[data-motion-feed]');
    const toggle = scope.querySelector('[data-feed-toggle]');
    if (feed && toggle && once(feed, 'Feed')) {
      const preference = matchMedia('(prefers-reduced-motion: reduce)');
      const clips = [...feed.querySelectorAll('[data-feed-video]')];
      let visible = false, paused = false;
      const sync = () => {
        const reduce = preference.matches || !!navigator.connection?.saveData;
        feed.classList.toggle('is-reduced', reduce);
        feed.classList.toggle('is-visible', visible && !document.hidden);
        feed.classList.toggle('is-paused', paused);
        toggle.textContent = reduce ? (toggle.dataset.reduced || 'Reduced motion · still gallery') : paused ? (toggle.dataset.play || 'Play movement ▶') : (toggle.dataset.pause || 'Pause movement Ⅱ');
        toggle.disabled = reduce;
        toggle.setAttribute('aria-pressed', String(paused || reduce));
        for (const v of clips) {
          const r = v.getBoundingClientRect();
          const onScreen = r.right > 0 && r.left < innerWidth && r.bottom > 0 && r.top < innerHeight;
          const play = visible && !paused && !reduce && !document.hidden && onScreen && !document.querySelector('dialog[open]');
          if (play) { if (!v.getAttribute('src')) v.src = v.dataset.src; v.play().catch(() => {}); } else v.pause();
        }
      };
      toggle.addEventListener('click', () => { paused = !paused; sync(); });
      preference.addEventListener('change', sync);
      document.addEventListener('visibilitychange', sync);
      new IntersectionObserver(entries => { visible = entries[0].isIntersecting; sync(); }, { threshold: 0 }).observe(feed);
      setInterval(() => { if (visible) sync(); }, 1100);
      sync();
    }
    /* phones: the creator row drifts sideways by itself (ping-pong), pauses while touched and when off screen, and is still swipeable */
    const rail = scope.querySelector('.creator-mosaic');
    if (rail && once(rail, 'Drift')) {
      const mobile = matchMedia('(max-width: 780px)'), calm = matchMedia('(prefers-reduced-motion: reduce)');
      let on = false, touching = false, dir = 1, resume = 0, x = 0, last = 0;
      new IntersectionObserver(entries => { on = entries[0].isIntersecting; }, { threshold: .2 }).observe(rail);
      const stop = () => { touching = true; };
      const go = () => { touching = false; resume = performance.now() + 3500; x = rail.scrollLeft; };
      rail.addEventListener('touchstart', stop, { passive: true }); rail.addEventListener('touchend', go, { passive: true });
      rail.addEventListener('pointerdown', stop); rail.addEventListener('pointerup', go);
      const tick = now => {
        const dt = Math.min(64, now - (last || now)); last = now;
        if (on && mobile.matches && !calm.matches && !touching && !document.hidden && now > resume && !document.querySelector('dialog[open]')) {
          const max = rail.scrollWidth - rail.clientWidth;
          if (max > 4) {
            x = Math.min(max, Math.max(0, x + dir * 22 * dt / 1000));
            rail.scrollLeft = x;
            if (x >= max || x <= 0) dir = -dir;
          }
        } else { x = rail.scrollLeft; }
        requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }
    const filters = [...scope.querySelectorAll('[data-creator-filter]')];
    if (filters.length && once(filters[0].closest('section') || filters[0], 'Filter')) {
      const section = filters[0].closest('section') || document;
      filters.forEach(button => button.addEventListener('click', () => {
        filters.forEach(b => b.setAttribute('aria-pressed', String(b === button)));
        section.querySelectorAll('[data-creator-keys]').forEach(card => {
          card.hidden = button.dataset.creatorFilter !== 'all' && !card.dataset.creatorKeys.split(',').includes(button.dataset.creatorFilter);
        });
      }));
    }
  }

  /* ---------- Video dialog (click-to-watch) ---------- */
  function videoDialog(trigger) {
    let dialog = document.getElementById('video-dialog');
    if (dialog) return dialog;
    const wrap = document.createElement('div');
    wrap.className = 'vvb';
    wrap.innerHTML = '<dialog id="video-dialog" aria-labelledby="video-title"><div class="dialog-top"><h2 id="video-title">A closer look.</h2><button type="button" data-close aria-label="Close video">×</button></div><div id="video-stage"></div></dialog>';
    if (trigger?.dataset.dialogTitle) wrap.querySelector('#video-title').textContent = trigger.dataset.dialogTitle;
    if (trigger?.dataset.dialogClose) wrap.querySelector('[data-close]').setAttribute('aria-label', trigger.dataset.dialogClose);
    document.body.appendChild(wrap);
    dialog = wrap.querySelector('dialog');
    dialog.querySelector('[data-close]').addEventListener('click', () => dialog.close());
    dialog.addEventListener('close', () => dialog.querySelector('#video-stage').replaceChildren());
    dialog.addEventListener('click', e => {
      if (e.target === dialog) {
        const r = dialog.getBoundingClientRect();
        if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) dialog.close();
      }
    });
    return dialog;
  }
  function initVideoDialog(scope = document) {
    scope.querySelectorAll('[data-video-src]').forEach(button => {
      if (!once(button, 'Dialog')) return;
      button.addEventListener('click', () => {
        const dialog = videoDialog(button);
        const video = document.createElement('video');
        video.controls = true; video.autoplay = true; video.playsInline = true; video.src = button.dataset.videoSrc;
        if (button.dataset.videoPoster) video.poster = button.dataset.videoPoster;
        if (button.dataset.videoTitle) dialog.querySelector('#video-title').textContent = button.dataset.videoTitle;
        dialog.querySelector('#video-stage').replaceChildren(video);
        dialog.showModal();
      });
    });
  }

  /* ---------- Brand film (autoplay when visible, user toggle) ---------- */
  function initBrandFilm(scope = document) {
    scope.querySelectorAll('.mindful-film').forEach(section => {
      const video = section.querySelector('video');
      const toggle = section.querySelector('[data-mindful-toggle]');
      if (!video || !toggle || !once(section, 'Film')) return;
      const reduced = matchMedia('(prefers-reduced-motion: reduce)');
      let pausedByUser = false, visible = false;
      const update = () => { toggle.textContent = video.paused ? (toggle.dataset.play || 'Play film ▶') : (toggle.dataset.pause || 'Pause film Ⅱ'); toggle.setAttribute('aria-pressed', String(!video.paused)); };
      const play = () => { if (!video.getAttribute('src')) video.src = video.dataset.src; video.play().then(update).catch(update); };
      toggle.addEventListener('click', () => { pausedByUser = !video.paused; if (video.paused) play(); else video.pause(); update(); });
      video.addEventListener('play', update); video.addEventListener('pause', update);
      new IntersectionObserver(entries => {
        visible = entries[0].isIntersecting;
        if (visible && !reduced.matches && !pausedByUser && !navigator.connection?.saveData) play(); else video.pause();
      }, { threshold: .3 }).observe(section);
      reduced.addEventListener('change', () => { if (reduced.matches) video.pause(); else if (visible && !pausedByUser) play(); });
      document.addEventListener('click', e => { if (e.target.closest('[data-video-src]')) video.pause(); });
    });
  }

  /* ---------- Newsletter interests -> customer tags ---------- */
  function initNewsletter(scope = document) {
    scope.querySelectorAll('.vvb-letter-form').forEach(form => {
      if (!once(form, 'Letter')) return;
      const tags = form.querySelector('[data-letter-tags]');
      const preview = form.querySelector('[data-letter-preview]');
      const boxes = [...form.querySelectorAll('[data-letter-interest]')];
      const names = { routines: 'easy routines and how-to guides', ingredients: 'ingredient explainers', news: 'brand news and launches' };
      const update = () => {
        const selected = boxes.filter(b => b.checked).map(b => b.value);
        if (tags) tags.value = ['newsletter', ...selected.map(s => 'interest-' + s)].join(',');
        if (preview) preview.textContent = selected.length ? 'Your edit: ' + selected.map(s => names[s]).join(' + ') + '.' : 'Choose what you would like to hear about.';
      };
      boxes.forEach(b => b.addEventListener('change', update));
      update();
    });
  }

  /* ---------- Interactive globe (drag, inertia, tilt, hover labels, click to fly) ---------- */
  function initGlobe(section) {
    const canvas = section.querySelector('[data-global-globe]');
    const ctx = canvas?.getContext('2d');
    const marketsEl = section.querySelector('#global-retail-markets');
    if (!canvas || !ctx || !marketsEl) return;
    const wrap = canvas.closest('.global-globe-wrap') || canvas.parentElement;
    const markets = JSON.parse(marketsEl.textContent);
    const status = section.querySelector('[data-globe-status]');
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    const rad = Math.PI / 180, seoul = [126.978, 37.5665];
    // Muted, distinguishable arc colours (cycled by market).
    const PALETTE = ['#d58a72', '#8f82c4', '#4d9ca6', '#cfa64f', '#c4708f', '#6389c6', '#7ea66b'];
    let land = [], rotation = 67, tilt = 20, vel = 0, selected = -1, hovered = -1;
    let width = 300, height = 300, frame = 0, last = 0, time = 0, gvisible = false;
    let drag = null, pausedUntil = 0, fly = null, dots = [], ripples = [], pointer = null;
    const vec = ([lon, lat]) => [Math.cos(lat * rad) * Math.cos(lon * rad), Math.cos(lat * rad) * Math.sin(lon * rad), Math.sin(lat * rad)];
    const project = ([lon, lat], lift = 1) => {
      const d = (lon - rotation) * rad, p = lat * rad, c = tilt * rad;
      return [Math.cos(p) * Math.sin(d) * lift, (Math.sin(p) * Math.cos(c) - Math.cos(p) * Math.cos(d) * Math.sin(c)) * lift, Math.sin(p) * Math.sin(c) + Math.cos(p) * Math.cos(d) * Math.cos(c)];
    };
    const wrapDeg = v => ((v + 540) % 360) - 180;
    const say = text => { if (status) status.textContent = text; };

    const draw = () => {
      const r = Math.min(width * .4, height * .45), cx = width / 2, cy = height * .5;
      const point = (p, lift = 1) => { const [x, y, z] = project(p, lift); return [cx + r * x, cy - r * y, z]; };
      ctx.clearRect(0, 0, width, height);
      // soft ground shadow + halo
      ctx.beginPath(); ctx.ellipse(cx, cy + r + 22, r * .8, 11, 0, 0, Math.PI * 2); ctx.fillStyle = '#2a2b3a12'; ctx.fill();
      const halo = ctx.createRadialGradient(cx, cy, r * .9, cx, cy, r * 1.22); halo.addColorStop(0, '#c9d6ea55'); halo.addColorStop(1, '#c9d6ea00');
      ctx.fillStyle = halo; ctx.beginPath(); ctx.arc(cx, cy, r * 1.22, 0, Math.PI * 2); ctx.fill();
      // ocean
      const ocean = ctx.createRadialGradient(cx - r * .45, cy - r * .5, r * .08, cx, cy, r);
      ocean.addColorStop(0, '#fbfcff'); ocean.addColorStop(.62, '#dde7f3'); ocean.addColorStop(1, '#b5c6dc');
      ctx.fillStyle = ocean; ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.fill();
      ctx.save(); ctx.clip();
      ctx.strokeStyle = '#7f95b52e'; ctx.lineWidth = .6;
      const line = points => { ctx.beginPath(); let started = false; for (const p of points) { const [x, y, z] = point(p); if (z > 0) { if (!started) ctx.moveTo(x, y); else ctx.lineTo(x, y); started = true; } else started = false; } ctx.stroke(); };
      for (let lat = -60; lat <= 60; lat += 30) line(Array.from({ length: 181 }, (_, i) => [-180 + i * 2, lat]));
      for (let lon = -180; lon < 180; lon += 30) line(Array.from({ length: 91 }, (_, i) => [lon, -90 + i * 2]));
      ctx.fillStyle = '#cdc6b6'; ctx.strokeStyle = '#ffffffb0'; ctx.lineWidth = .45;
      for (const polygon of land) {
        const ps = polygon.map(p => project(p)); if (!ps.some(p => p[2] > 0)) continue;
        ctx.beginPath(); ps.forEach(([x, y, z], i) => { if (z < 0) { const m = Math.hypot(x, y) || 1; x /= m; y /= m; } const px = cx + r * x, py = cy - r * y; if (i) ctx.lineTo(px, py); else ctx.moveTo(px, py); });
        ctx.closePath(); ctx.fill(); ctx.stroke();
      }
      // limb shading
      const shade = ctx.createRadialGradient(cx - r * .35, cy - r * .4, r * .4, cx, cy, r);
      shade.addColorStop(0, '#ffffff00'); shade.addColorStop(1, '#5f76a22e');
      ctx.fillStyle = shade; ctx.fillRect(cx - r, cy - r, r * 2, r * 2);
      ctx.restore();

      const origin = vec(seoul);
      dots = [];
      const dragging = !!(drag && drag.moved);
      const ordered = markets.map((m, i) => i).sort((a, b) => (a === selected) - (b === selected));
      for (const i of ordered) {
        const [name, lon, lat] = markets[i], color = PALETTE[i % PALETTE.length];
        const destination = [lon, lat], target = vec(destination), dot = Math.max(-1, Math.min(1, origin.reduce((s, v, j) => s + v * target[j], 0))), omega = Math.acos(dot), sin = Math.sin(omega);
        const arc = t => { const a = sin ? Math.sin((1 - t) * omega) / sin : 1 - t, b = sin ? Math.sin(t * omega) / sin : t, v = origin.map((n, j) => a * n + b * target[j]); return point([Math.atan2(v[1], v[0]) / rad, Math.atan2(v[2], Math.hypot(v[0], v[1])) / rad], 1 + .18 * Math.sin(Math.PI * t)); };
        const hot = i === selected || i === hovered;
        ctx.save();
        ctx.beginPath(); let started = false;
        for (let j = 0; j <= 48; j++) { const [x, y, z] = arc(j / 48); if (z > -.03) { if (!started) ctx.moveTo(x, y); else ctx.lineTo(x, y); started = true; } else started = false; }
        ctx.strokeStyle = color + (hot ? 'ff' : dragging ? 'bb' : '80');
        ctx.lineWidth = (i === selected ? 2.4 : i === hovered ? 2 : .95) + (dragging ? .3 : 0);
        ctx.lineCap = 'round';
        if (hot) { ctx.shadowColor = color; ctx.shadowBlur = 9; }
        ctx.stroke(); ctx.restore();
        const t = (time / (dragging ? 3800 : 7000) + i * .13) % 1, p = arc(t), before = arc(Math.max(0, t - .025));
        if (p[2] > 0) { const angle = Math.atan2(p[1] - before[1], p[0] - before[0]); ctx.save(); ctx.translate(p[0], p[1]); ctx.rotate(angle); ctx.fillStyle = color; ctx.beginPath(); ctx.moveTo(5, 0); ctx.lineTo(-3.5, -3); ctx.lineTo(-3.5, 3); ctx.closePath(); ctx.fill(); ctx.restore(); }
        const [x, y, z] = point(destination);
        if (z > 0) {
          dots.push({ i, x, y });
          ctx.beginPath(); ctx.arc(x, y, i === selected ? 5.6 : hot ? 4.8 : 3.4, 0, Math.PI * 2); ctx.fillStyle = color; ctx.fill();
          ctx.lineWidth = 1.4; ctx.strokeStyle = '#ffffff'; ctx.stroke();
          if (i === selected) { const ring = 7 + ((time / 70) % 16); ctx.beginPath(); ctx.arc(x, y, ring, 0, Math.PI * 2); ctx.strokeStyle = color + Math.round(255 * (1 - (ring - 7) / 16)).toString(16).padStart(2, '0'); ctx.lineWidth = 1.5; ctx.stroke(); }
        }
      }
      // Seoul
      const [sx, sy, sz] = point(seoul);
      if (sz > 0) {
        const pulse = (time / (dragging ? 260 : 500));
        ctx.strokeStyle = '#e2705c66'; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.arc(sx, sy, 9 + 3 * Math.sin(pulse), 0, Math.PI * 2); ctx.stroke();
        ctx.fillStyle = '#2f2a3a'; ctx.beginPath(); ctx.arc(sx, sy, 5.2, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = '#e2705c'; ctx.beginPath(); ctx.arc(sx, sy, 2.4, 0, Math.PI * 2); ctx.fill();
        ctx.font = '700 10px Arial'; ctx.textAlign = 'center'; ctx.fillStyle = '#2f2a3a'; ctx.fillText('SEOUL', sx, sy - 14);
      }
      // label pills for hovered / selected markets
      const label = (i) => {
        const d = dots.find(o => o.i === i); if (!d) return;
        const name = markets[i][0], color = PALETTE[i % PALETTE.length];
        ctx.font = '600 11px Arial'; const w = ctx.measureText(name).width + 18, h = 22, x = d.x - w / 2, y = d.y - 38;
        ctx.save(); ctx.shadowColor = '#2a2b3a33'; ctx.shadowBlur = 8; ctx.fillStyle = '#fffffff0'; ctx.beginPath(); ctx.roundRect(x, y, w, h, 11); ctx.fill(); ctx.restore();
        ctx.strokeStyle = color; ctx.lineWidth = 1.2; ctx.beginPath(); ctx.roundRect(x, y, w, h, 11); ctx.stroke();
        ctx.fillStyle = '#2f2a3a'; ctx.textAlign = 'center'; ctx.fillText(name, d.x, y + 15);
      };
      if (hovered >= 0 && hovered !== selected) label(hovered);
      if (selected >= 0) label(selected);
      // click ripples
      ripples = ripples.filter(rp => time - rp.t0 < 900);
      for (const rp of ripples) { const k = (time - rp.t0) / 900; ctx.beginPath(); ctx.arc(rp.x, rp.y, 6 + k * 46, 0, Math.PI * 2); ctx.strokeStyle = '#8f82c4' + Math.round(200 * (1 - k)).toString(16).padStart(2, '0'); ctx.lineWidth = 2 * (1 - k) + .4; ctx.stroke(); }
      canvas.dataset.rotation = rotation.toFixed(2); canvas.dataset.tilt = tilt.toFixed(1); canvas.dataset.rendered = 'true';
    };

    const size = () => { const rect = canvas.getBoundingClientRect(); width = rect.width || 300; height = rect.height || 300; const dpr = Math.min(devicePixelRatio || 1, 2); canvas.width = Math.round(width * dpr); canvas.height = Math.round(height * dpr); ctx.setTransform(dpr, 0, 0, dpr, 0, 0); draw(); };
    const auto = () => gvisible && section.classList.contains('is-motion-active') && !reduced.matches;
    const busy = () => !!(drag || fly || Math.abs(vel) > .0006 || ripples.length || auto());
    const step = now => {
      frame = 0;
      const dt = last ? Math.min(now - last, 50) : 16; last = now; time += dt;
      if (fly) {
        const k = Math.min(1, (now - fly.t0) / fly.dur), e = 1 - Math.pow(1 - k, 3);
        rotation = fly.r0 + fly.dr * e; tilt = fly.t0v + fly.dt * e;
        if (k >= 1) fly = null;
      } else if (!drag) {
        if (Math.abs(vel) > .0006) { rotation += vel * dt; vel *= Math.pow(.94, dt / 16); }
        else if (auto() && now > pausedUntil) rotation += dt * .004;
      }
      draw();
      if (busy() && gvisible) frame = requestAnimationFrame(step); else last = 0;
    };
    const wake = () => { if (!frame && gvisible) frame = requestAnimationFrame(step); else if (!gvisible) draw(); };
    const touch = () => { pausedUntil = performance.now() + 3000; wrap.classList.add('is-touched'); };
    const flyTo = i => {
      const [name, lon, lat] = markets[i];
      selected = i;
      const mid = seoul[0] + wrapDeg(lon - seoul[0]) * .5, midLat = (lat + seoul[1]) / 2;
      fly = { r0: rotation, dr: wrapDeg(mid - rotation), t0: performance.now(), dur: 950, t0v: tilt, dt: Math.max(-10, Math.min(45, midLat * .55)) - tilt };
      say('Seoul → ' + name);
      touch(); wake();
    };
    const nearest = (x, y, max = 13) => { let best = -1, bd = max; for (const d of dots) { const dd = Math.hypot(d.x - x, d.y - y); if (dd < bd) { bd = dd; best = d.i; } } return best; };
    const local = e => { const r = canvas.getBoundingClientRect(); return [e.clientX - r.left, e.clientY - r.top]; };

    canvas.addEventListener('pointerdown', e => {
      const [x, y] = local(e);
      drag = { x, y, rotation, tilt, moved: false, lx: x, lt: performance.now(), v: 0 };
      vel = 0; fly = null; canvas.setPointerCapture(e.pointerId); canvas.classList.add('is-dragging'); touch(); wake();
    });
    canvas.addEventListener('pointermove', e => {
      const [x, y] = local(e);
      if (drag) {
        const dx = x - drag.x, dy = y - drag.y;
        if (Math.hypot(dx, dy) > 4) drag.moved = true;
        rotation = drag.rotation - dx * .36; tilt = Math.max(-28, Math.min(58, drag.tilt + dy * .22));
        const now = performance.now(), dtm = Math.max(1, now - drag.lt);
        drag.v = (drag.lx - x) * .36 / dtm; drag.lx = x; drag.lt = now;
        touch(); draw();
      } else if (e.pointerType === 'mouse') {
        const h = nearest(x, y);
        if (h !== hovered) { hovered = h; canvas.style.cursor = h >= 0 ? 'pointer' : ''; wake(); draw(); }
      }
    });
    const release = e => {
      if (!drag) return;
      const [x, y] = local(e);
      const wasDrag = drag.moved, v = drag.v;
      drag = null; canvas.classList.remove('is-dragging');
      if (wasDrag) { vel = Math.max(-.12, Math.min(.12, v)); }
      else {
        const hit = nearest(x, y, e.pointerType === 'mouse' ? 13 : 22);
        if (hit >= 0) flyTo(hit);
        else { ripples.push({ x, y, t0: time }); vel = (Math.random() < .5 ? -1 : 1) * .05; selected = -1; touch(); }
      }
      wake();
    };
    canvas.addEventListener('pointerup', release); canvas.addEventListener('pointercancel', release);
    canvas.addEventListener('pointerleave', () => { if (hovered !== -1 && !drag) { hovered = -1; canvas.style.cursor = ''; draw(); } });
    canvas.addEventListener('keydown', e => {
      if (['ArrowLeft', 'ArrowRight'].includes(e.key)) { e.preventDefault(); rotation += e.key === 'ArrowRight' ? 12 : -12; touch(); draw(); }
      else if (['ArrowUp', 'ArrowDown'].includes(e.key)) { e.preventDefault(); tilt = Math.max(-28, Math.min(58, tilt + (e.key === 'ArrowUp' ? -8 : 8))); touch(); draw(); }
      else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); flyTo((selected + 1) % markets.length); }
      else if (e.key === 'Escape') { selected = -1; say(''); draw(); }
    });
    new MutationObserver(wake).observe(section, { attributes: true, attributeFilter: ['class'] });
    new IntersectionObserver(es => { gvisible = es[0].isIntersecting; if (gvisible) wake(); }).observe(canvas);
    new ResizeObserver(size).observe(canvas); size();
    fetch(canvas.dataset.landUrl).then(r => { if (!r.ok) throw Error('Globe map unavailable'); return r.json(); }).then(data => { land = data.filter(poly => Math.max(...poly.map(q => q[1])) > -60); draw(); }).catch(() => say('Globe map detail unavailable.'));
  }

  /* ---------- Global presence (photo wall dialog + globe) ---------- */
  function initGlobal(scope = document) {
    const section = scope.querySelector('.global-presence');
    if (!section || !once(section, 'Global')) return;
    const photos = [...section.querySelectorAll('[data-global-photo]')];
    const dialog = section.querySelector('#global-photo-dialog');
    const motion = section.querySelector('[data-global-motion]');
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    const connection = navigator.connection;
    let active = 0, opener = null, visible = false, paused = false;
    const sync = () => section.classList.toggle('is-motion-active', visible && !paused && !reduced.matches && !connection?.saveData && !document.hidden && !document.querySelector('dialog[open]'));
    if (dialog && photos.length) {
      const image = dialog.querySelector('[data-global-large]'), caption = dialog.querySelector('#global-photo-caption');
      const counter = dialog.querySelector('[data-global-counter]'), source = dialog.querySelector('[data-global-source]');
      const showPhoto = i => {
        active = (i + photos.length) % photos.length;
        const p = photos[active];
        image.src = p.dataset.large; image.alt = p.querySelector('img').alt;
        caption.textContent = p.dataset.caption;
        if (p.dataset.story) { source.href = p.dataset.story; source.hidden = false; } else source.hidden = true;
        counter.textContent = String(active + 1).padStart(2, '0') + ' / ' + photos.length;
      };
      photos.forEach((p, i) => p.addEventListener('click', () => { opener = p; showPhoto(i); dialog.showModal(); sync(); }));
      dialog.querySelector('[data-global-close]').addEventListener('click', () => dialog.close());
      dialog.querySelector('[data-global-prev]').addEventListener('click', () => showPhoto(active - 1));
      dialog.querySelector('[data-global-next]').addEventListener('click', () => showPhoto(active + 1));
      dialog.addEventListener('keydown', e => {
        if (e.key === 'ArrowRight') { e.preventDefault(); showPhoto(active + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); showPhoto(active - 1); }
      });
      dialog.addEventListener('click', e => {
        if (e.target === dialog) {
          const r = dialog.getBoundingClientRect();
          if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) dialog.close();
        }
      });
      dialog.addEventListener('close', () => { image.removeAttribute('src'); opener?.focus({ preventScroll: true }); sync(); });
    }
    motion?.addEventListener('click', () => { paused = !paused; motion.setAttribute('aria-pressed', String(paused)); motion.textContent = paused ? 'Resume motion ▶' : 'Pause motion Ⅱ'; sync(); });
    if ('IntersectionObserver' in window) new IntersectionObserver(entries => { visible = entries[0].isIntersecting; sync(); }, { threshold: 0 }).observe(section);
    else { visible = true; sync(); }
    document.addEventListener('visibilitychange', sync);
    reduced.addEventListener('change', sync);
    new MutationObserver(sync).observe(document.body, { subtree: true, attributes: true, attributeFilter: ['open'] });

    initGlobe(section);
  }


  /* ---------- Collection filter + sort ---------- */
  function initCollection(scope = document) {
    const grid = scope.querySelector('[data-vvb-grid]');
    if (!grid || !once(grid, 'Grid')) return;
    const root = grid.closest('section');
    const input = root.querySelector('[data-vvb-filter]'), sort = root.querySelector('[data-vvb-sort]');
    const results = root.querySelector('[data-vvb-results]'), empty = root.querySelector('[data-vvb-empty]');
    const cards = [...grid.children];
    input?.addEventListener('input', () => {
      const q = input.value.trim().toLowerCase();
      let n = 0;
      cards.forEach(c => { const show = !q || (c.dataset.cardTitle || '').includes(q); c.hidden = !show; if (show) n++; });
      if (results) results.textContent = results.dataset.one && results.dataset.other ? (n === 1 ? results.dataset.one : results.dataset.other).replace('#', n) : n + ' product' + (n === 1 ? '' : 's');
      if (empty) empty.hidden = n !== 0;
    });
    root.querySelector('[data-vvb-topic]')?.addEventListener('change', e => { location.href = e.target.value; });
    sort?.addEventListener('change', () => { const url = new URL(location.href); url.searchParams.set('sort_by', sort.value); url.searchParams.delete('page'); location.href = url; });
  }


  /* ---------- Add to bag from product cards: AJAX add + open Beyond's cart drawer ---------- */
  function initCartForms(scope = document) {
    scope.querySelectorAll('form[action*="/cart/add"]').forEach(form => {
      if (!form.closest('.vvb') || !once(form, 'Cart')) return;
      form.addEventListener('submit', async e => {
        if (!window.trigger) return;
        e.preventDefault();
        const button = form.querySelector('button[type=submit]');
        button?.setAttribute('aria-busy', 'true');
        try {
          const id = Number(new FormData(form).get('id'));
          const r = await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id, quantity: 1 }], sections: 'cart-drawer' }) });
          if (!r.ok) throw new Error('add failed');
          const data = await r.json();
          const cart = await (await fetch('/cart.js')).json();
          document.querySelectorAll('[data-cart-count]').forEach(el => el.setAttribute('data-cart-count', cart.item_count));
          document.querySelector('.layout--viewport')?.setAttribute('data-cart-empty', String(cart.item_count === 0));
          Object.entries(data.sections || {}).forEach(([key, html]) => window.trigger('theme:cart:' + key.replace(/^cart-/, '') + ':updateHtml', { section: html, load_transitions: true }));
          setTimeout(() => window.trigger('theme:cart:open'), 250);
        } catch (err) {
          form.submit();
        } finally { button?.removeAttribute('aria-busy'); }
      });
    });
  }

  /* ---------- Shared helpers: product JSON + add to bag (opens Beyond's cart drawer) ---------- */
  const money = cents => new Intl.NumberFormat('en-US', { style: 'currency', currency: window.Shopify?.currency?.active || 'USD' }).format(cents / 100);
  const esc = s => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const productJson = handle => fetch(((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'products/' + handle + '.js').then(r => { if (!r.ok) throw new Error('product'); return r.json(); });
  async function addItemsToBag(ids) {
    const r = await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: ids.map(id => ({ id, quantity: 1 })), sections: 'cart-drawer' }) });
    if (!r.ok) throw new Error('add failed');
    const data = await r.json();
    const cart = await (await fetch('/cart.js')).json();
    document.querySelectorAll('[data-cart-count]').forEach(el => el.setAttribute('data-cart-count', cart.item_count));
    document.querySelector('.layout--viewport')?.setAttribute('data-cart-empty', String(cart.item_count === 0));
    Object.entries(data.sections || {}).forEach(([key, html]) => window.trigger?.('theme:cart:' + key.replace(/^cart-/, '') + ':updateHtml', { section: html, load_transitions: true }));
    setTimeout(() => window.trigger?.('theme:cart:open'), 250);
  }

  /* ---------- Skin quiz (five visual questions -> a routine from real products) ---------- */
  const QUIZ_HANDLES = { foam: 'vvbetter-5-5-soothing-cleansing-foam', vitac: 'dark-spot-solution-vita-c-trx-serum', teca: 'teca-lifting-moisture-cream', pdrn: 'firming-bakuchiol-pdrn-serum-30ml', bubble: 'jeju-yuja-balancing-bubble-cleanser-145-ml', succinic: 'jeju-yuja-succinic-balancing-serum-30ml', cera: 'jeju-yuja-cera-balancing-cream', 'teca-serum': 'teca-lifting-mositure-serum' };
  function initQuiz(scope = document) {
    const form = scope.querySelector('#quiz-form');
    if (!form || !once(form, 'Quiz')) return;
    const steps = [...form.querySelectorAll('.quiz-step')], resultBox = scope.querySelector('#quiz-result');
    let configured = {}; try { configured = JSON.parse(form.dataset.products || '{}'); } catch (e) { /* keep defaults */ }
    const handles = { ...QUIZ_HANDLES, ...Object.fromEntries(Object.entries(configured).filter(([, v]) => v)) };
    const q = s => form.querySelector(s);
    const T0 = {
      selected: 'Selected: {title}. Continue when you are ready.', choose_option: 'Choose an option to continue.', building: 'Building your routine…',
      load_failed: 'We could not load the products just now. Please try again.', eyebrow: 'YOUR STARTING POINT', headline: 'A routine with a reason.',
      minimal_note: 'You asked for fewer steps, so we start with cleansing and moisture instead of adding an optional serum.',
      reactive_notice: 'You prefer a cautious introduction. Introduce one unfamiliar product at a time and use one serum per routine. This preference tool does not assess skin conditions.',
      step: 'STEP', morning_note: 'Morning: finish with appropriate sunscreen.', disclaimer: 'These are optional product suggestions, not a diagnosis. Final prices and availability are shown at checkout.',
      add_routine: 'Add this routine · {price}', edit_choices: 'Edit my choices ↗', start_again: 'Start again',
      feel: { dry: 'You described skin that often feels dry. Keep a moisturizing finish in mind.', oily: 'You described skin that often looks shiny. Compare the textures before adding another step.', combination: 'You described a little of both. Notice which areas prefer more or less product.', unsure: 'You are still discovering your preferences. Start small and use the instructions as your guide.' },
      reason: { tone: 'Vita C TRX is positioned for uneven-looking tone. The cleanser and moisturizer support the sequence.', firmness: 'Bakuchiol PDRN gives this routine a firmness-focused serum step. Shake before use.', balance: 'The suggested Jeju Yuja steps focus on balance, with clear application instructions.', hydration: 'A cleanser, moisture-focused serum and cream make the sequence easy to follow.' }
    };
    let T = T0; try { T = { ...T0, ...JSON.parse(form.dataset.i18n || '{}') }; } catch (e) { /* keep English defaults */ }
    const labelNext = q('[data-quiz-next]').dataset.labelNext || 'Continue ↗', labelFinal = q('[data-quiz-next]').dataset.labelFinal || 'See my starting point ↗';
    const hint = q('[data-choice-feedback]').dataset.hint || 'Choose the card that feels most like you.';
    let step = 0;
    const move = () => {
      steps.forEach((x, i) => { x.hidden = i !== step; });
      q('[data-quiz-progress]').textContent = String(step + 1).padStart(2, '0') + ' / 05';
      q('.quiz-progress i').style.width = (step + 1) * 20 + '%';
      q('[data-quiz-back]').hidden = step === 0;
      q('[data-quiz-next]').textContent = step === 4 ? labelFinal : labelNext;
      q('#quiz-message').textContent = '';
      q('[data-choice-feedback]').textContent = hint;
    };
    form.querySelectorAll('input[type=radio]').forEach(input => input.addEventListener('change', () => { q('[data-choice-feedback]').textContent = T.selected.replace('{title}', input.closest('label').querySelector('b').textContent); }));
    const result = async () => {
      const a = Object.fromEntries(new FormData(form));
      let ids = a.goal === 'tone' ? ['foam', 'vitac', 'teca'] : a.goal === 'firmness' ? ['foam', 'pdrn', 'teca'] : a.goal === 'balance' ? ['bubble', 'succinic', 'cera'] : ['foam', 'teca-serum', 'teca'];
      if (a.steps === 'minimal') ids = [a.goal === 'balance' ? 'bubble' : 'foam', a.goal === 'balance' ? 'cera' : 'teca'];
      if (a.sensitivity === 'reactive' && a.goal === 'balance') ids = a.steps === 'minimal' ? ['foam', 'cera'] : ['foam', 'succinic', 'cera'];
      const note = T.feel[a.feel] || '';
      const reason = T.reason[a.goal] || '';
      q('#quiz-message').textContent = T.building;
      let products;
      try { products = await Promise.all(ids.map(id => productJson(handles[id]))); } catch (e) { q('#quiz-message').textContent = T.load_failed; return; }
      form.hidden = true; resultBox.hidden = false;
      const total = products.reduce((n, p) => n + p.price, 0);
      resultBox.innerHTML = '<p class="eyebrow">' + esc(T.eyebrow) + ' / ' + esc(String(a.time || '').toUpperCase()) + '</p><h2>' + esc(T.headline) + '</h2><p>' + esc(note) + '</p><p>' + esc(a.steps === 'minimal' ? T.minimal_note : reason) + '</p>'
        + (a.sensitivity === 'reactive' ? '<aside class="notice">' + esc(T.reactive_notice) + '</aside>' : '')
        + products.map((p, i) => '<div class="quiz-result-product"><img src="' + esc(p.featured_image) + '" alt="' + esc(p.title) + '" width="160" height="160"><div><span class="eyebrow">' + esc(T.step) + ' ' + (i + 1) + '</span><h3><a href="' + esc(p.url) + '">' + esc(p.title) + ' ↗</a></h3><p>' + esc(p.type) + ' · ' + money(p.price) + '</p></div></div>').join('')
        + '<p class="small">' + esc((a.time !== 'pm' ? T.morning_note + ' ' : '') + T.disclaimer) + '</p><div class="actions"><button class="button" type="button" id="quiz-add">' + esc(T.add_routine).replace('{price}', money(total)) + '</button><a class="button secondary" href="/pages/build-a-routine">' + esc(T.edit_choices) + '</a></div><button class="text-button" type="button" id="quiz-restart">' + esc(T.start_again) + '</button>';
      resultBox.querySelector('#quiz-add').addEventListener('click', async e => { e.currentTarget.setAttribute('aria-busy', 'true'); try { await addItemsToBag(products.map(p => p.variants[0].id)); } catch (err) { location.href = '/cart'; } e.currentTarget.removeAttribute('aria-busy'); });
      resultBox.querySelector('#quiz-restart').addEventListener('click', () => { form.hidden = false; resultBox.hidden = true; step = 0; move(); });
    };
    q('[data-quiz-next]').addEventListener('click', () => {
      if (!steps[step].querySelector('input:checked')) { q('#quiz-message').textContent = T.choose_option; return; }
      if (step === 4) result(); else { step++; move(); }
    });
    q('[data-quiz-back]').addEventListener('click', () => { step = Math.max(0, step - 1); move(); });
    form.addEventListener('submit', e => e.preventDefault());
    move();
  }

  /* ---------- Routine builder (choose up to three real products) ---------- */
  function initBuilder(scope = document) {
    const grid = scope.querySelector('[data-builder-grid]');
    if (!grid || !once(grid, 'Builder')) return;
    const root = grid.closest('section') || document;
    const totalEl = root.querySelector('[data-builder-total]'), addBtn = root.querySelector('[data-builder-add]');
    const handles = (grid.dataset.handles || '').split(',').filter(Boolean);
    const state = new Map();
    const refresh = () => {
      const picked = [...state.values()].filter(x => x.checked);
      totalEl.textContent = money(picked.reduce((n, x) => n + x.p.price, 0));
      addBtn.disabled = !picked.length;
      state.forEach(x => { x.input.disabled = !x.checked && picked.length >= 3; });
    };
    Promise.all(handles.map(h => productJson(h).catch(() => null))).then(list => {
      list.filter(Boolean).forEach(p => {
        const card = document.createElement('article');
        card.className = 'builder-card';
        card.innerHTML = '<img src="' + esc(p.featured_image) + '" alt="' + esc(p.title) + '" width="300" height="300" loading="lazy"><label><input type="checkbox" data-builder="' + esc(p.handle) + '"><span>' + esc(p.title) + '<small>' + money(p.price) + ' · ' + esc(p.type) + '</small></span></label>';
        const input = card.querySelector('input');
        const rec = { p, input, checked: false };
        input.addEventListener('change', () => { rec.checked = input.checked; refresh(); });
        state.set(p.handle, rec); grid.appendChild(card);
      });
      refresh();
    });
    addBtn.addEventListener('click', async () => {
      const picked = [...state.values()].filter(x => x.checked);
      if (!picked.length) return;
      addBtn.setAttribute('aria-busy', 'true');
      try { await addItemsToBag(picked.map(x => x.p.variants[0].id)); } catch (e) { location.href = '/cart'; }
      addBtn.removeAttribute('aria-busy');
    });
  }

  /* ---------- Product page: ingredient tabs + morning/evening routine switch ---------- */
  function initPdp(scope = document) {
    const tabs = [...scope.querySelectorAll('[data-ingredient-tab]')];
    if (tabs.length && once(tabs[0], 'IngredientTabs')) {
      const select = tab => tabs.forEach(t => {
        const active = t === tab;
        t.setAttribute('aria-selected', String(active)); t.tabIndex = active ? 0 : -1;
        const panel = document.getElementById(t.getAttribute('aria-controls')); if (panel) panel.hidden = !active;
      });
      tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => select(tab));
        tab.addEventListener('keydown', e => {
          let next;
          if (e.key === 'ArrowRight') next = (i + 1) % tabs.length;
          if (e.key === 'ArrowLeft') next = (i - 1 + tabs.length) % tabs.length;
          if (e.key === 'Home') next = 0;
          if (e.key === 'End') next = tabs.length - 1;
          if (next !== undefined) { e.preventDefault(); select(tabs[next]); tabs[next].focus(); }
        });
      });
    }
    scope.querySelectorAll('[data-routine-time]').forEach(btn => {
      if (!once(btn, 'RoutineTime')) return;
      btn.addEventListener('click', () => {
        const group = btn.closest('.routine-planner') || document;
        group.querySelectorAll('[data-routine-time]').forEach(b => b.setAttribute('aria-pressed', String(b === btn)));
        const copy = group.querySelector('[data-routine-time-copy]');
        if (copy) copy.textContent = btn.dataset.routineTime === 'am' ? copy.dataset.am : copy.dataset.pm;
      });
    });
  }

  /* ---------- Blog article: automatic table of contents from the H2 / H3 headings ---------- */
  function initArticle(scope = document) {
    const toc = scope.querySelector('[data-article-toc]');
    const body = scope.querySelector('[data-article-body]');
    if (!toc || !body || !once(toc, 'Toc')) return;
    const heads = [...body.querySelectorAll('h2, h3')].filter(h => h.textContent.trim() && !h.closest('details'));
    if (heads.length < (Number(toc.dataset.min) || 3)) return;
    const used = new Set();
    const slug = text => text.toLowerCase().replace(/&/g, ' and ').replace(/[^a-z0-9ㄱ-ㆎ가-힣]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60) || 'section';
    heads.forEach(h => { let id = h.id || slug(h.textContent); let n = 2; const base = id; while (used.has(id)) id = base + '-' + n++; used.add(id); h.id = id; });
    let html = '<strong>' + (toc.dataset.title || 'In this article') + '</strong><ol>';
    let open = false, sub = false;
    heads.forEach(h => {
      const link = '<a href="#' + h.id + '">' + h.textContent.trim().replace(/[<>&]/g, c => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[c])) + '</a>';
      if (h.tagName === 'H2') {
        if (sub) { html += '</ol>'; sub = false; }
        if (open) html += '</li>';
        html += '<li>' + link; open = true;
      } else {
        if (!open) { html += '<li>'; open = true; }
        if (!sub) { html += '<ol>'; sub = true; }
        html += '<li>' + link + '</li>';
      }
    });
    if (sub) html += '</ol>';
    if (open) html += '</li>';
    html += '</ol>';
    toc.innerHTML = html;
    toc.hidden = false;
  }

  const initAll = (scope = document) => { initNav(scope); initHero(scope); initMotion(scope); initVideoDialog(scope); initBrandFilm(scope); initNewsletter(scope); initGlobal(scope); initCollection(scope); initCartForms(scope); initQuiz(scope); initBuilder(scope); initPdp(scope); initArticle(scope); window.dispatchEvent(new CustomEvent('vvb:init', { detail: scope })); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();
  document.addEventListener('shopify:section:load', e => initAll(e.target));
})();
